import { memo, ReactNode } from "react";
import { Stack, Theme } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useTranslation } from "next-i18next";
import { ArrowLink } from "src/components-mui/base/arrow-link/arrow-link";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { ListMotiveDescription } from "src/components-mui/modules/underlying-lists-module/components/list-motive-description";
import { ListTable } from "src/components-mui/modules/underlying-lists-module/components/list-table";
import { ListTrendWikifolio } from "src/components-mui/modules/underlying-lists-module/components/list-trend-wikifolio";
import {
    UNDERLYING_LIST_MOTIVE_CONFIG,
    UNDERLYING_LIST_VISIBLE_ITEMS_COUNT,
    UnderlyingListDaysCount,
    UnderlyingListMotive,
    UnderlyingListOverlayPlacement,
    getUnderlyingListMotiveColor,
} from "src/components-mui/modules/underlying-lists-module/underlying-lists-module.constants";
import {
    UNDERLYING_LIST_DESCRIPTION_TEST_ID,
    UNDERLYING_LIST_FULL_LIST_LINK_TEST_ID,
    UNDERLYING_LIST_HEADING_TEST_ID,
    UNDERLYING_LIST_TEST_ID,
} from "src/components-mui/modules/underlying-lists-module/underlying-lists-module.test-ids";
import { IUnderlyingList } from "src/components-mui/modules/underlying-lists-module/underlying-lists-module.types";
import { useUserStore } from "src/stores/user-store";

interface IListProps {
    id: string;
    motive: UnderlyingListMotive;
    list: IUnderlyingList;
    daysCount: UnderlyingListDaysCount;
    isSelected: boolean;
    expandedIsin: string | null;
    onItemToggle: ({ motive, isin }: { motive: UnderlyingListMotive; isin: string }) => void;
    overlay: ReactNode | null;
    overlayPlacement: UnderlyingListOverlayPlacement;
}

const ListComponent = ({
    id,
    motive,
    list: { items, link, trendWikifolio },
    daysCount,
    isSelected,
    expandedIsin,
    onItemToggle,
    overlay,
    overlayPlacement,
}: IListProps) => {
    const { t } = useTranslation("underlying-overview");
    const { isLoggedIn } = useUserStore();
    const headingId = `${id}-heading`;
    const shouldShowFullListLink = isLoggedIn && !!link?.url && overlay === null;

    return (
        <Stack
            component="section"
            id={id}
            aria-labelledby={headingId}
            sx={{ display: { xs: isSelected ? "flex" : "none", md: "flex" }, gap: 3 }}
            data-test-id={`${UNDERLYING_LIST_TEST_ID}-${motive}`}
        >
            <WfHeading
                id={headingId}
                component="h3"
                fontSize="2xl"
                sx={(theme: Theme) => ({ [theme.breakpoints.down("md")]: visuallyHidden })}
                data-test-id={`${UNDERLYING_LIST_HEADING_TEST_ID}-${motive}`}
            >
                {t(UNDERLYING_LIST_MOTIVE_CONFIG[motive].titleKey)}
            </WfHeading>
            <Stack sx={{ gap: 4 }}>
                <Stack sx={{ flexGrow: 1, gap: 2 }}>
                    <ListMotiveDescription motive={motive} dataTestId={`${UNDERLYING_LIST_DESCRIPTION_TEST_ID}-${motive}`} />
                    <ListTable
                        items={items.slice(0, UNDERLYING_LIST_VISIBLE_ITEMS_COUNT)}
                        daysCount={daysCount}
                        motive={motive}
                        motiveColor={getUnderlyingListMotiveColor(motive)}
                        expandedIsin={expandedIsin}
                        onItemToggle={onItemToggle}
                        overlay={overlay}
                        overlayPlacement={overlayPlacement}
                        ariaLabelledBy={headingId}
                    />
                    {shouldShowFullListLink && (
                        <ArrowLink
                            href={link.url}
                            target={link.target ?? undefined}
                            sx={{ alignSelf: "flex-end", fontSize: "md", fontWeight: "bold", color: "gray.800" }}
                            data-test-id={`${UNDERLYING_LIST_FULL_LIST_LINK_TEST_ID}-${motive}`}
                        >
                            {link.name}
                        </ArrowLink>
                    )}
                </Stack>
                {trendWikifolio && <ListTrendWikifolio wikifolio={trendWikifolio} />}
            </Stack>
        </Stack>
    );
};

export const List = memo(ListComponent);
